import React, { useEffect, useState } from "react";
import {
  TextField,
  Button,
  Box,
  Grid,
  MenuItem,
  CircularProgress,
  Typography,
  Paper,
  Chip,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Collapse,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  CallSplit as CallSplitIcon,
  Storefront as StorefrontIcon,
  ShoppingCart as ShoppingCartIcon,
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
} from "@mui/icons-material";
import DeleteIcon from "@mui/icons-material/Delete";
import { useDispatch } from "react-redux";
import useAlert from "../../../../hooks/useAlert";
import { validatePlaceProductDefinationRule } from "../../../../utils/validation";
import {
  GetPlaceTriggerDefinations,
  AddPlaceTriggerDefination,
  DeletePlaceTriggerDefination,
  GetPlaceProductDefinationRule,
  AddPlaceProductDefinationRule,
  UpdatePlaceProductDefinationRule,
  DeletePlaceProductDefinationRule,
} from "../../../../redux/actions/productSyncActions";
import SectionCard from "../SectionCard";

const RULE_TYPE_OPTIONS = [
  { value: 0, label: "Yüzde (%)" },
  { value: 1, label: "Sabit Tutar (₺)" },
];

const resolvePlace = (defination, placeOptions) => {
  const key = defination.marketPlaceId
    ? `mp-${defination.marketPlaceId}`
    : `ec-${defination.ecommercePlaceId}`;
  return placeOptions.find((o) => o.key === key);
};

const textFieldSx = { "& .MuiOutlinedInput-root": { borderRadius: 1.5 } };

const primaryButtonSx = {
  borderRadius: 1.5,
  backgroundColor: "#1F1B45",
  color: "#FFFFFF",
  textTransform: "none",
  fontWeight: 600,
  fontSize: { xs: "0.813rem", sm: "0.875rem" },
  padding: { xs: "8px 20px", sm: "10px 24px" },
  boxShadow: "none",
  "&:hover": { backgroundColor: "#2C285A", boxShadow: "none" },
};

const TriggerTargetList = ({ triggerId, placeOptions, baseKey }) => {
  const dispatch = useDispatch();
  const { showSuccessAlertAllNoReload, showErrorAlertAll } = useAlert();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [definations, setDefinations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addKey, setAddKey] = useState("");
  const [adding, setAdding] = useState(false);
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [savingId, setSavingId] = useState(null);
  const [formErrors, setFormErrors] = useState({});

  const fetchDefinations = async () => {
    try {
      setLoading(true);
      const list = await dispatch(GetPlaceTriggerDefinations(triggerId));
      const withRules = await Promise.all(
        (list || []).map(async (d) => {
          const rules = await dispatch(GetPlaceProductDefinationRule(d.id));
          const rule = rules && rules.length > 0 ? rules[0] : { plus: true, figure: "", type: 0 };
          return { ...d, rule };
        })
      );
      setDefinations(withRules);
      setExpandedIds(new Set(withRules.map((d) => d.id)));
    } catch (error) {
      console.error("Hedef pazaryerleri alınamadı:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (triggerId) fetchDefinations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerId]);

  const usedKeys = new Set([
    baseKey,
    ...definations.map((d) => (d.marketPlaceId ? `mp-${d.marketPlaceId}` : `ec-${d.ecommercePlaceId}`)),
  ]);
  const availableOptions = placeOptions.filter((opt) => !usedKeys.has(opt.key));

  const handleAddTarget = async (e) => {
    e.preventDefault();
    if (!addKey) return;
    const [sourceType, rawId] = addKey.split("-");
    const payload = {
      triggerId,
      marketPlaceId: sourceType === "mp" ? Number(rawId) : null,
      ecommercePlaceId: sourceType === "ec" ? Number(rawId) : null,
      ia: true,
    };
    try {
      setAdding(true);
      await dispatch(AddPlaceTriggerDefination(payload));
      showSuccessAlertAllNoReload();
      setAddKey("");
      await fetchDefinations();
    } catch {
      showErrorAlertAll();
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (defination) => {
    const confirmDelete = window.confirm(
      "Bu hedef pazaryerini ve varsa fiyat kuralını silmek istediğinizden emin misiniz?"
    );
    if (!confirmDelete) return;
    try {
      if (defination.rule?.id) {
        await dispatch(DeletePlaceProductDefinationRule(defination.rule.id));
      }
      await dispatch(DeletePlaceTriggerDefination(defination.id));
      showSuccessAlertAllNoReload();
      await fetchDefinations();
    } catch {
      showErrorAlertAll();
    }
  };

  const toggleExpanded = (definationId) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      next.has(definationId) ? next.delete(definationId) : next.add(definationId);
      return next;
    });
  };

  const handleRuleFieldChange = (definationId, field, value) => {
    setDefinations((prev) =>
      prev.map((d) => (d.id === definationId ? { ...d, rule: { ...d.rule, [field]: value } } : d))
    );
  };

  const handleSaveRule = async (defination) => {
    const errors = validatePlaceProductDefinationRule(defination.rule);
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    try {
      setSavingId(defination.id);
      // Kaydetme anında sunucudan taze kontrol yapılır; local state bayat olsa bile
      // zaten bir kural varsa mutlaka UPDATE (PUT), yoksa CREATE (POST) atılır.
      const existingRules = await dispatch(GetPlaceProductDefinationRule(defination.id));
      const existingRule = existingRules && existingRules.length > 0 ? existingRules[0] : null;

      const payload = {
        definationId: defination.id,
        plus: !!defination.rule.plus,
        figure: Number(defination.rule.figure),
        type: Number(defination.rule.type),
        ia: true,
      };
      if (existingRule) payload.id = existingRule.id;

      existingRule
        ? await dispatch(UpdatePlaceProductDefinationRule(payload))
        : await dispatch(AddPlaceProductDefinationRule(payload));
      showSuccessAlertAllNoReload();
      await fetchDefinations();
    } catch {
      showErrorAlertAll();
    } finally {
      setSavingId(null);
    }
  };

  return (
    <SectionCard
      icon={CallSplitIcon}
      title="Hedef Pazaryerleri ve Fiyat Kuralları"
      description="Ürünlerin aktarılacağı platformları ekleyin, her biri için ayrı bir fiyat kuralı tanımlayın"
    >
      <Box component="form" onSubmit={handleAddTarget}>
        <Grid container spacing={{ xs: 1.5, sm: 2 }} alignItems="center">
          <Grid item xs={12} sm={8}>
            <TextField
              select
              size={isMobile ? "small" : "medium"}
              fullWidth
              label="Hedef Pazaryeri / E-ticaret Ekle"
              value={addKey}
              onChange={(e) => setAddKey(e.target.value)}
              disabled={availableOptions.length === 0}
              sx={textFieldSx}
            >
              {availableOptions.map((opt) => (
                <MenuItem key={opt.key} value={opt.key}>
                  {opt.name} ({opt.sourceType === "marketplace" ? "Pazaryeri" : "E-ticaret"})
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4} sx={{ display: "flex", justifyContent: { xs: "flex-start", sm: "flex-end" } }}>
            <Button
              type="submit"
              variant="contained"
              disabled={adding || !addKey}
              sx={{ ...primaryButtonSx, minWidth: { xs: "120px", sm: "140px" } }}
            >
              {adding ? <CircularProgress size={20} sx={{ color: "inherit" }} /> : "Hedef Ekle"}
            </Button>
          </Grid>
        </Grid>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
          <CircularProgress />
        </Box>
      ) : (
        definations.length > 0 && (
          <Grid container spacing={{ xs: 1.5, sm: 2 }} sx={{ mt: { xs: 2, sm: 2.5 } }}>
            {definations.map((defination) => {
              const place = resolvePlace(defination, placeOptions);
              const isMarketplace = place?.sourceType === "marketplace";
              const isExpanded = expandedIds.has(defination.id);
              return (
                <Grid item xs={12} sm={6} key={defination.id}>
                  <Paper
                    variant="outlined"
                    sx={{
                      borderRadius: 1.5,
                      borderColor: isExpanded ? "#1F1B45" : "#e0e0e0",
                      overflow: "hidden",
                      transition: "border-color 0.15s ease",
                    }}
                  >
                    <Box
                      onClick={() => toggleExpanded(defination.id)}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        px: 1.5,
                        py: 1,
                        cursor: "pointer",
                        backgroundColor: isExpanded ? "rgba(31, 27, 69, 0.05)" : "#fafafa",
                        "&:hover": { backgroundColor: "rgba(31, 27, 69, 0.05)" },
                      }}
                    >
                      {isMarketplace ? (
                        <StorefrontIcon fontSize="small" sx={{ color: "#1F1B45" }} />
                      ) : (
                        <ShoppingCartIcon fontSize="small" sx={{ color: "#1F1B45" }} />
                      )}
                      <Typography sx={{ fontWeight: 600, fontSize: "0.9rem", flexGrow: 1 }}>
                        {place?.name || "Bilinmeyen"}
                      </Typography>
                      <Chip
                        size="small"
                        label={isMarketplace ? "Pazaryeri" : "E-ticaret"}
                        sx={{ fontSize: "0.7rem" }}
                      />
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(defination);
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                      <ExpandMoreIcon
                        fontSize="small"
                        sx={{
                          transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                          transition: "transform 0.15s ease",
                        }}
                      />
                    </Box>
                    <Collapse in={isExpanded}>
                      <Box sx={{ p: 1.5 }}>
                        <Typography
                          variant="caption"
                          sx={{ display: "block", color: "text.secondary", mb: 1.5 }}
                        >
                          Fiyat kuralı zorunlu değildir. Bu hedefte fiyatın kaynak pazaryeriyle
                          birebir aynı kalmasını istiyorsanız Miktar alanına 0 girip kaydedebilir,
                          ya da bu formu hiç kaydetmeyebilirsiniz.
                        </Typography>
                        <Grid container spacing={1.5} alignItems="center">
                          <Grid item xs={12}>
                            <ToggleButtonGroup
                              size="small"
                              exclusive
                              fullWidth
                              value={defination.rule.plus ? "plus" : "minus"}
                              onChange={(e, val) => {
                                if (val) handleRuleFieldChange(defination.id, "plus", val === "plus");
                              }}
                            >
                              <ToggleButton value="plus" sx={{ textTransform: "none" }}>
                                <AddIcon fontSize="small" sx={{ mr: 0.5 }} /> Fiyata Ekle
                              </ToggleButton>
                              <ToggleButton value="minus" sx={{ textTransform: "none" }}>
                                <RemoveIcon fontSize="small" sx={{ mr: 0.5 }} /> Fiyattan Çıkar
                              </ToggleButton>
                            </ToggleButtonGroup>
                          </Grid>
                          <Grid item xs={6}>
                            <TextField
                              size="small"
                              fullWidth
                              type="number"
                              label="Miktar"
                              value={defination.rule.figure}
                              onChange={(e) =>
                                handleRuleFieldChange(defination.id, "figure", e.target.value)
                              }
                              error={!!formErrors.figure}
                              helperText={formErrors.figure}
                              sx={textFieldSx}
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <TextField
                              select
                              size="small"
                              fullWidth
                              label="Tip"
                              value={defination.rule.type}
                              onChange={(e) =>
                                handleRuleFieldChange(defination.id, "type", e.target.value)
                              }
                              sx={textFieldSx}
                            >
                              {RULE_TYPE_OPTIONS.map((opt) => (
                                <MenuItem key={opt.value} value={opt.value}>
                                  {opt.label}
                                </MenuItem>
                              ))}
                            </TextField>
                          </Grid>
                          <Grid item xs={12} sx={{ display: "flex", justifyContent: "flex-end" }}>
                            <Button
                              variant="contained"
                              onClick={() => handleSaveRule(defination)}
                              disabled={savingId === defination.id}
                              sx={primaryButtonSx}
                            >
                              {savingId === defination.id ? (
                                <CircularProgress size={18} sx={{ color: "inherit" }} />
                              ) : (
                                "Kuralı Kaydet"
                              )}
                            </Button>
                          </Grid>
                        </Grid>
                      </Box>
                    </Collapse>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        )
      )}
    </SectionCard>
  );
};

export default TriggerTargetList;
